<script setup lang="ts">
import type { ButtonAlignment } from '~/enums/button-alignment'
import type { Button } from '~/types'

interface Props {
  anchor?: string
  title?: string
  paragraph: string
  buttonsAlignment?: ButtonAlignment.LEFT
  buttons?: Button[]
}

defineProps<Props>()
</script>

<template>
  <section
    v-if="paragraph"
    :id="anchor"
    class="paragraph"
  >
    <div class="wrapper">
      <h2
        v-if="title"
        class="paragraph__title"
      >
        {{ title }}
      </h2>
      <div v-html="paragraph" />
      <div
        v-if="buttons"
        class="paragraph__buttons"
      >
        <CommonButtons
          :alignment="buttonsAlignment"
          :buttons="buttons"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.paragraph {
  @apply text-dark-text text-[16px] leading-[1.7] my-[40px];
  @apply md:my-[60px] md:text-[18px];
  @apply lg:my-[90px];

  & > * {
    @apply mb-4 last:mb-0;
    @apply md:mb-6;
    @apply xl:mb-8;
  }

  &__title {
    @apply mb-4;
    @apply md:mb-6;
    @apply xl:mb-8;
  }

  ul,
  ol {
    @apply ml-4;

    li {
      @apply mb-2 last:mb-0;
    }
  }

  ul {
    @apply list-disc;
  }

  ol {
    @apply list-decimal;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply mb-4 last:mb-0 font-medium leading-[1.1] text-dark-text;
    @apply md:mb-6;
    @apply xl:mb-8;

    strong {
      @apply font-medium;
    }
  }

  h1,
  h2 {
    @apply text-[26px];
    @apply md:text-[30px];
    @apply lg:text-[40px];
    @apply xl:text-[60px];
  }

  h3 {
    @apply text-[24px];
    @apply md:text-[28px];
    @apply xl:text-[34px];
    @apply 2xl:text-[40px];
    @apply 3xl:text-[44px];
  }

  h4 {
    @apply text-[20px];
    @apply md:text-[24px];
    @apply xl:text-[30px];
    @apply 2xl:text-[34px];
    @apply 3xl:text-[38px];
  }

  h5 {
    @apply text-[18px];
    @apply md:text-[20px];
    @apply xl:text-[24px];
    @apply 2xl:text-[28px];
    @apply 3xl:text-[32px];
  }

  h6 {
    @apply text-[16px];
    @apply md:text-[18px];
    @apply xl:text-[22px];
    @apply 2xl:text-[24px];
    @apply 3xl:text-[26px];
  }

  a:not(.button) {
    @apply underline duration-300;
    @apply hover:text-dark-text;
  }

  .table-responsive {
    @apply overflow-x-auto relative;
  }

  table {
    @apply w-auto min-w-full max-w-full h-fit;

    tbody,
    thead {
      @apply w-full;
    }

    td,
    th {
      @apply px-4 py-2;
    }

    td {
      @apply text-[14px] text-dark-text font-medium;
      @apply xl:text-[16px];

      a {
        @apply inline-block font-medium;
      }
    }

    tr {
      @apply odd:bg-gray-100 border-b border-black first:border-t border-x transition-colors duration-300;

      td,
      th {
        @apply leading-normal align-middle;
      }

      th {
        @apply text-[12px] uppercase font-bold bg-dark-background text-light-text text-left;
        @apply xl:text-[14px];
      }
    }
  }

  iframe {
    @apply w-full h-[250px] block #{!important};
    @apply md:h-[360px] #{!important};
    @apply lg:h-[560px] #{!important};
  }

  &__buttons {
    @apply w-full mt-[20px];
    @apply md:mt-[30px];
  }
}
</style>
